import type { TargetingProps } from '@openx/types/targeting/targeting.ts';
import {
  type TechnologyAndDevicesData,
  type TechnologyAndDevicesFormState,
  TechnologyAndDevicesOption,
  type UserAgentPayload,
} from '@openx/types/targeting/technologyAndDevices.ts';
import { noop } from '@openx/utils/noop.ts';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { type JSX, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomOptionsProvider } from '../../utils/hooks/useCustomOptionsContext.tsx';
import { useRemoveParameterUpdater } from '../../utils/hooks/useRemoveParameterUpdater.ts';
import type { CtvValidationReturnType } from '../shared/CtvValidation/utils/useCtvValidation.ts';
import { DimensionChipLine } from '../shared/DimensionChip/DimensionChipLine.tsx';
import { EmptyFilterMessage } from '../shared/EmptyFilterMessage/EmptyFilterMessage.tsx';
import { useGetFilteredFields } from '../shared/hooks/useGetFilteredFields.ts';
import { usePreselectableDrawer } from '../shared/hooks/usePreselectableDrawer.ts';
import { TargetingDrawer } from '../shared/TargetingDrawer/TargetingDrawer.tsx';
import { useDrawer } from '../shared/TargetingDrawer/useDrawer.tsx';
import { isEmptyState } from '../shared/Technographic/utils.ts';
import { technographicDefaultOptions } from './constants.ts';
import { Items } from './Items.tsx';
import { TechnologyAndDevicesReadOnly } from './TechnologyAndDevicesReadOnly.tsx';
import { TopBox } from './TopBox.tsx';
import { mapTechnographicToApi, mapTechnographicToFormState } from './utils.ts';

type CtvValidationMethods = Omit<
  CtvValidationReturnType,
  'setFormatType' | 'isFormatTypeOpen' | 'shouldDisplayWarningInFormatType'
>;

const defaultCustomValidationMethods: CtvValidationMethods = {
  isTechnographicOpen: false,
  setIsFormatTypeOpen: noop,
  setIsTechnographicOpen: noop,
  setTechnographic: noop,
  shouldDisplayWarningInTechnographic: false,
};

export const TechnologyAndDevices = memo(function TechnologyAndDevices({
  targetingParams,
  onFieldUpdate = noop,
  name,
  readonly,
  field,
  customValidationMethods = defaultCustomValidationMethods,
  customOptions = technographicDefaultOptions,
  displayContext,
  isNewFormat = true,
}: TargetingProps<TechnologyAndDevicesData, CtvValidationMethods>): JSX.Element {
  const { t } = useTranslation();
  const initState = mapTechnographicToFormState(cloneDeep(targetingParams));
  const [params, updateParams] = useState<TechnologyAndDevicesFormState>(initState);
  const [type, onTypeChange] = useState<TechnologyAndDevicesOption>(
    customOptions.defaultOption ?? TechnologyAndDevicesOption.API_FRAMEWORK
  );
  const { setTechnographic, setIsTechnographicOpen, isTechnographicOpen, ...methods } = customValidationMethods;

  useEffect(() => {
    setTechnographic(params);
  }, [params, setTechnographic]);

  const isDataUpdated = useMemo(
    () => !isEqual(targetingParams, mapTechnographicToApi(params)),
    [params, targetingParams]
  );

  const { isOpen, onDrawerClose, onDrawerOpen, onDrawerApply } = useDrawer({
    onApply: () => {
      onFieldUpdate(field, mapTechnographicToApi(params));
      setIsTechnographicOpen(false);
    },
    restoreParams: () => updateParams({ ...initState }),
  });

  const isValidFormat = (type: string): type is TechnologyAndDevicesOption => {
    return Object.values(TechnologyAndDevicesOption).includes(type as TechnologyAndDevicesOption);
  };

  const openDrawerWithPreselectedType = usePreselectableDrawer<TechnologyAndDevicesOption>(
    isValidFormat,
    onDrawerOpen,
    onTypeChange
  );

  const onRemoveAll = useCallback(() => {
    onFieldUpdate(field, null);
    updateParams(mapTechnographicToFormState(null));
  }, [onFieldUpdate, field]);

  const onRemoveParameter = useRemoveParameterUpdater<
    TechnologyAndDevicesFormState,
    TechnologyAndDevicesOption,
    TechnologyAndDevicesData,
    typeof field
  >({
    field,
    onFieldUpdate,
    setState: updateParams,
    getDefaults: () => mapTechnographicToFormState(null),
    mapStateToApi: mapTechnographicToApi,
  });

  const onChange = useCallback(
    (options: Set<string> | UserAgentPayload, op?: string) => {
      if (options instanceof Set) {
        updateParams(params => ({ ...params, [type]: { op: op ?? params[type].op, val: options } }));
        return;
      }

      if (type === TechnologyAndDevicesOption.USER_AGENT) {
        updateParams(params => ({
          ...params,
          [TechnologyAndDevicesOption.USER_AGENT]: {
            op: options.op,
            val: options.val,
          },
        }));
      }
    },
    [type]
  );

  const targetingParamsFiltered = useGetFilteredFields(params || {});
  const isEmpty = isEmptyState(params);
  const selectedItems =
    type === TechnologyAndDevicesOption.USER_AGENT ? params[TechnologyAndDevicesOption.USER_AGENT] : params[type].val;

  return (
    <CustomOptionsProvider value={customOptions}>
      <TechnologyAndDevicesReadOnly
        name={name}
        readonly={readonly}
        targetingParams={initState}
        onOpenDrawerClick={(type?: string) => {
          openDrawerWithPreselectedType(type);
          setIsTechnographicOpen(true);
        }}
        field={field}
        onRemoveClick={onRemoveAll}
        onRemoveParameter={onRemoveParameter}
        customValidationMethods={methods}
        displayContext={displayContext}
        isNewFormat={isNewFormat}
      />
      {(isOpen || isTechnographicOpen) && (
        <TargetingDrawer
          name={name}
          onClose={() => {
            onDrawerClose();
            setIsTechnographicOpen(false);
          }}
          onApply={onDrawerApply}
          isApplyDisabled={!isDataUpdated}
        >
          <TopBox
            items={selectedItems}
            onChange={onChange}
            selectedType={type}
            onTypeChange={onTypeChange}
            isNewFormat={isNewFormat}
            displayContext={displayContext}
          />

          {isEmpty && <EmptyFilterMessage text={t('No filters applied.')} />}

          {!isEmpty &&
            targetingParamsFiltered.map((type, index) => {
              const shouldAddDimensionChipLine = targetingParamsFiltered.length - 1 > index;

              return (
                <div key={type}>
                  <Items
                    targetingParams={params}
                    readonly={false}
                    type={type}
                    onChange={updateParams}
                    displayContext={displayContext}
                  />

                  {shouldAddDimensionChipLine && <DimensionChipLine />}
                </div>
              );
            })}
        </TargetingDrawer>
      )}
    </CustomOptionsProvider>
  );
});
